import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { addDays, differenceInMinutes, endOfDay, endOfMonth, endOfWeek, format, isSameDay, isWithinInterval, startOfDay, startOfMonth, startOfWeek, subWeeks } from 'date-fns';
import { find, sortBy } from 'lodash';
import Client from '@/model/entity/client/client';
import Decimal from 'big.js';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import Job from '@/model/entity/job/job';
import Timesheet from '@/model/entity/audit/timesheet';
import TimesheetService from '@/model/service/audit/timesheet-service';

@Component
export default class TimesheetBreakdown extends Vue
{
    @Prop()
    private userId!: string;

    @Prop()
    private date!: Date;

    private period = 'monthly';
    private form: FormInterface|null = null;

    private lookupPeriod: {from: Date; to: Date}|null = null;
    private timesheets: Timesheet[] = [];

    private listener = () =>
    {
        this.loadTimesheets();
    };

    private created()
    {
        this.lookupPeriod = this.calculateLookupPeriod(this.date);

        this.generateForm();
        this.loadTimesheets();

        EventBus.instance.$on('timesheet-updated', this.listener);
    }

    private beforeDestroy()
    {
        EventBus.instance.$off('timesheet-updated', this.listener);
    }

    @Watch('userId')
    private onUserChanged()
    {
        this.loadTimesheets();
    }

    @Watch('date')
    private onDateChanged()
    {
        const newLookupPeriod = this.calculateLookupPeriod(this.date);

        if (!this.lookupPeriod || !isSameDay(newLookupPeriod.from, this.lookupPeriod.from) || !isSameDay(newLookupPeriod.to, this.lookupPeriod.to))
        {
            this.lookupPeriod = newLookupPeriod;
            this.loadTimesheets();
        }
    }

    /**
     * Calculates the lookup period
     *
     * @param date
     * @returns
     */

    private calculateLookupPeriod(date: Date): {from: Date; to: Date}
    {
        return {
            from: subWeeks(startOfMonth(date), 1),
            to: endOfMonth(date),
        };
    }

    /**
     * Returns the current breakdown period
     */

    private get breakdownPeriod(): {from: Date; to: Date}
    {
        switch (this.period)
        {
        case 'daily':
            return {
                from: startOfDay(this.date),
                to: endOfDay(this.date),
            };

        case 'weekly':
            return {
                from: startOfWeek(this.date, { weekStartsOn: 1 }),
                to: endOfWeek(this.date, { weekStartsOn: 1 }),
            };

        case 'monthly':
        default:
            return {
                from: startOfMonth(this.date),
                to: endOfMonth(this.date),
            };
        }
    }

    /**
     * Returns the worked total for the day
     */

    private get dayTotal(): number
    {
        const from = startOfDay(this.date);
        const to = endOfDay(this.date);

        let minutes = 0;

        for (const timesheet of this.timesheets)
        {
            if (!timesheet.dateFrom || !timesheet.dateTo || !timesheet.job || !isWithinInterval(timesheet.dateFrom, {
                start: from,
                end: to,
            }))
            {
                continue;
            }

            minutes += differenceInMinutes(timesheet.dateTo, timesheet.dateFrom);
        }

        return minutes;
    }

    /**
     * Returns the worked total for the week
     */

    private get weekTotal(): number
    {
        const from = startOfWeek(this.date, { weekStartsOn: 1 });
        const to = endOfWeek(this.date, { weekStartsOn: 1 });

        let minutes = 0;

        for (const timesheet of this.timesheets)
        {
            if (!timesheet.dateFrom || !timesheet.dateTo || !timesheet.job || !isWithinInterval(timesheet.dateFrom, {
                start: from,
                end: to,
            }))
            {
                continue;
            }

            minutes += differenceInMinutes(timesheet.dateTo, timesheet.dateFrom);
        }

        return minutes;
    }

    /**
     * Returns the task breakdown
     */

    private get taskBreakdown(): {job: Job; task: string; minutes: number}[]
    {
        let breakdown: {job: Job; task: string; minutes: number}[] = [];

        for (const timesheet of this.timesheets)
        {
            if (!timesheet.job || !timesheet.dateFrom || !timesheet.dateTo || !isWithinInterval(timesheet.dateFrom, {
                start: this.breakdownPeriod.from,
                end: this.breakdownPeriod.to,
            }))
            {
                continue;
            }

            // Check for an existing task
            let task = find(breakdown, task =>
            {
                const isSameJob = !!((timesheet.job && task.job && task.job.id === timesheet.job.id));
                const isSameTask = !!((!timesheet.job && task.task === timesheet.task));

                return isSameJob || isSameTask;
            });

            if (task)
            {
                task.minutes += differenceInMinutes(timesheet.dateTo, timesheet.dateFrom);
            }
            else
            {
                task =
                {
                    job: timesheet.job,
                    task: timesheet.task,
                    minutes: differenceInMinutes(timesheet.dateTo, timesheet.dateFrom),
                };

                breakdown.push(task);
            }
        }

        // Sort by task name
        breakdown = sortBy(breakdown, [ task => (task.job ? task.job.name : task.task) ]);

        return breakdown;
    }

    /**
     * Returns the client breakdown
     */

    private get clientBreakdown(): {client: Client; minutes: number}[]
    {
        let breakdown: {client: Client; minutes: number}[] = [];

        for (const timesheet of this.timesheets)
        {
            // Skip if no job or client
            if (!timesheet.job || !timesheet.job.client || !timesheet.dateFrom || !timesheet.dateTo || !isWithinInterval(timesheet.dateFrom, {
                start: this.breakdownPeriod.from,
                end: this.breakdownPeriod.to,
            }))
            {
                continue;
            }

            // Check for an existing item
            let item = find(breakdown, currentItem => currentItem.client.id === timesheet.job?.client?.id);

            if (item)
            {
                item.minutes += differenceInMinutes(timesheet.dateTo, timesheet.dateFrom);
            }
            else
            {
                item =
                {
                    client: timesheet.job.client,
                    minutes: differenceInMinutes(timesheet.dateTo, timesheet.dateFrom),
                };

                breakdown.push(item);
            }
        }

        // Sort by client name
        breakdown = sortBy(breakdown, [ item => item.client.name ]);

        return breakdown;
    }

    /**
     * Returns the job type breakdown
     */

    private get jobTypeBreakdown(): {chargeable: string; minutes: number}[]
    {
        let breakdown: {chargeable: string; minutes: number}[] = [];

        for (const timesheet of this.timesheets)
        {
            // Not interested in not working
            if (timesheet.chargeable === 'n/a' || !timesheet.dateFrom || !timesheet.dateTo || !isWithinInterval(timesheet.dateFrom, {
                start: this.breakdownPeriod.from,
                end: this.breakdownPeriod.to,
            }))
            {
                continue;
            }

            // Check for an existing item
            let item = find(breakdown, { chargeable: timesheet.chargeable });

            if (item)
            {
                item.minutes += differenceInMinutes(timesheet.dateTo, timesheet.dateFrom);
            }
            else
            {
                item =
            {
                chargeable: timesheet.chargeable,
                minutes: differenceInMinutes(timesheet.dateTo, timesheet.dateFrom),
            };

                breakdown.push(item);
            }
        }

        // Sort by chargeable
        breakdown = sortBy(breakdown, [ item => item.chargeable ]);

        return breakdown;
    }

    /**
     * Returns time spent on a client in a readable format.
     *
     * @param minutes
     */

    private getReadableTime(minutes: number): string
    {
        if (!minutes)
        {
            return '00:00';
        }

        const decimal = new Decimal(minutes);

        const remainingMinutes = decimal.mod(60);
        const hours = (new Decimal(decimal)).minus(remainingMinutes)
            .div(60)
            .round();

        return `${ hours.toString().padStart(2, '0') }:${ remainingMinutes.toString().padStart(2, '0') }`;
    }

    /**
     * Returns time spent on a client in a verbose readable format.
     *
     * @param minutes
     */

    private getReadableTimeVerbose(minutes: number): string
    {
        if (!minutes)
        {
            return '0 hours';
        }

        const decimal = new Decimal(minutes);

        const remainingMinutes = decimal.mod(60);
        const hours = (new Decimal(decimal)).minus(remainingMinutes)
            .div(60)
            .round();

        let time = '';

        if (hours.gt(1))
        {
            time += `${ hours } hours`;
        }
        else if (hours.eq(1))
        {
            time += `${ hours } hour`;
        }

        if (remainingMinutes.gt(1))
        {
            time += `${ (time ? ' and ' : '') + remainingMinutes } minutes`;
        }
        else if (remainingMinutes.eq(1))
        {
            time += `${ (time ? ' and ' : '') + remainingMinutes } minute`;
        }

        return time;
    }

    /**
     * Returns the title for a chargeable value
     *
     * @param value
     */

    public getChargeableTitle(value: string): string
    {
        return TimesheetService.getChargeableTitle(value);
    }

    /**
     * Loads the timesheets for the current month, minus 1 week to cater for weekly breakdown
     */

    private async loadTimesheets()
    {
        if (!this.lookupPeriod)
        {
            this.timesheets = [];
            return;
        }

        const results = await this.$serviceContainer.timesheetService.findTimesheets({
            'user': this.userId,
            'dateFrom[after]': format(this.lookupPeriod.from, 'yyyy-MM-dd'),
            'dateFrom[strictly_before]': format(addDays(this.lookupPeriod.to, 1), 'yyyy-MM-dd'),
            'order': [
                {
                    key: 'dateFrom',
                    descending: false,
                },
                {
                    key: 'dateCreated',
                    descending: false,
                },
            ],
            'itemsPerPage': 10000,
        });

        this.timesheets = results.results;
    }

    /**
     * Handles form field value changes
     *
     * @param fieldValues
     * @returns
     */

    private onValuesChanged(fieldValues: FormFieldValuesInterface)
    {
        this.period = fieldValues.period as string;
    }

    /**
     * Generates the form from the timesheet
     */

    private generateForm()
    {
        const form: FormInterface =
        {
            items: [
                {
                    key: 'period',
                    type: 'string',
                    title: this.$t('timesheet.field.breakdown').toString(),
                    hideTitle: true,
                    value: this.period,
                    required: true,
                    options: [
                        {
                            value: 'daily',
                            title: this.$t('timesheet.field.daily').toString(),
                        },
                        {
                            value: 'weekly',
                            title: this.$t('timesheet.field.weekly').toString(),
                        },
                        {
                            value: 'monthly',
                            title: this.$t('timesheet.field.monthly').toString(),
                        },
                    ],
                    dropdown: true,
                },
            ],
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Handles job clicks
     *
     * @param job
     */

    private onJobClick(job: Job)
    {
        EventBus.instance.$emit('preview-job', job.id);
    }
}